import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { BuyerSearchFilters as FilterType } from "@/types/buyerSearch";
import { PURCHASING_TYPES, CLOSE_TIMES } from "@/constants/buyerFormOptions";
import { usStates } from "@/utils/states";
import { Filter, X } from "lucide-react";

interface BuyerSearchFiltersProps {
  filters: FilterType;
  onFiltersChange: (filters: FilterType) => void;
  onSearch: () => void;
  onReset: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const BuyerSearchFilters = ({
  filters,
  onFiltersChange,
  onSearch,
  onReset,
  isCollapsed = false,
  onToggleCollapse
}: BuyerSearchFiltersProps) => {
  const [dealsRange, setDealsRange] = useState([
    filters.dealsAnnuallyMin || 0,
    filters.dealsAnnuallyMax || 100
  ]);

  const handlePurchasingTypeChange = (type: string, checked: boolean) => {
    const currentTypes = filters.purchasingTypes || [];
    const newTypes = checked 
      ? [...currentTypes, type]
      : currentTypes.filter(t => t !== type);
    
    onFiltersChange({
      ...filters,
      purchasingTypes: newTypes.length > 0 ? newTypes : undefined
    });
  };

  const handleMarketChange = (market: string, checked: boolean) => {
    const currentMarkets = filters.preferredMarkets || [];
    const newMarkets = checked 
      ? [...currentMarkets, market]
      : currentMarkets.filter(m => m !== market);
    
    onFiltersChange({
      ...filters,
      preferredMarkets: newMarkets.length > 0 ? newMarkets : undefined
    });
  };

  const handleDealsRangeChange = (values: number[]) => {
    setDealsRange(values);
    onFiltersChange({
      ...filters,
      dealsAnnuallyMin: values[0] > 0 ? values[0] : undefined,
      dealsAnnuallyMax: values[1] < 100 ? values[1] : undefined
    });
  };

  const hasActiveFilters = () => {
    return !!(
      filters.purchasingTypes?.length ||
      filters.preferredMarkets?.length ||
      filters.averageCloseTime ||
      filters.companyName ||
      filters.dealsAnnuallyMin ||
      filters.dealsAnnuallyMax
    );
  };

  if (isCollapsed) {
    return (
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">
                {hasActiveFilters() ? "Filters Active" : "No Filters"}
              </span>
              {hasActiveFilters() && (
                <span className="text-xs text-muted-foreground">
                  ({Object.keys(filters).filter(key => filters[key]).length} applied)
                </span>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={onToggleCollapse}>
              Show Filters
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Search Filters
        </CardTitle>
        {onToggleCollapse && (
          <Button variant="ghost" size="sm" onClick={onToggleCollapse}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Company Name */}
        <div className="space-y-2">
          <Label htmlFor="companyName">Company Name</Label>
          <Input
            id="companyName"
            placeholder="Search by company name..."
            value={filters.companyName || ""}
            onChange={(e) => onFiltersChange({
              ...filters,
              companyName: e.target.value || undefined
            })}
          />
        </div>

        {/* Purchasing Types */}
        <div className="space-y-3">
          <Label>Purchasing Types</Label>
          <div className="grid grid-cols-2 gap-2">
            {PURCHASING_TYPES.map((type) => (
              <div key={type} className="flex items-center space-x-2">
                <Checkbox
                  id={`purchasing-${type}`}
                  checked={filters.purchasingTypes?.includes(type) || false}
                  onCheckedChange={(checked) => 
                    handlePurchasingTypeChange(type, !!checked)
                  }
                />
                <Label 
                  htmlFor={`purchasing-${type}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {type}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Average Close Time */}
        <div className="space-y-2">
          <Label>Average Close Time</Label>
          <Select
            value={filters.averageCloseTime || "any"}
            onValueChange={(value) => onFiltersChange({
              ...filters,
              averageCloseTime: value === "any" ? undefined : value
            })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select close time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {CLOSE_TIMES.map((time) => (
                <SelectItem key={time} value={time}>
                  {time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Deals Annually */}
        <div className="space-y-3">
          <Label>Deals Annually: {dealsRange[0]} - {dealsRange[1] === 100 ? "100+" : dealsRange[1]}</Label>
          <Slider
            value={dealsRange}
            onValueChange={handleDealsRangeChange}
            max={100}
            min={0}
            step={1}
            className="w-full"
          />
        </div>

        {/* Preferred Markets */}
        <div className="space-y-3">
          <Label>Preferred Markets</Label>
          <div className="max-h-40 overflow-y-auto grid grid-cols-2 gap-2">
            {usStates.map(({ name, id }) => (
              <div key={id} className="flex items-center space-x-2">
                <Checkbox
                  id={`market-${id}`}
                  checked={filters.preferredMarkets?.includes(name) || false}
                  onCheckedChange={(checked) => 
                    handleMarketChange(name, !!checked)
                  }
                />
                <Label 
                  htmlFor={`market-${id}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {name}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-4">
          <Button onClick={onSearch} className="flex-1" data-tour="search-button">
            Search Buyers
          </Button>
          <Button variant="outline" onClick={onReset} disabled={!hasActiveFilters()}>
            Clear All
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};